import React, { useEffect, useRef, useState } from 'react';

function ProblemSelector({
  selectedCell,
  problemList,
  problemDetails,
  loadingProblemDetails,
  selectedProblems,
  onProblemClick,
  onReorderProblem,
  onRemoveProblem,
  currentPage,
  totalPages,
  onPageChange,
  perPage = 20,
  isMobileView = false,
  isProblemEditMode = false,
  searchTerm = '',
  showOnlySelected = false
}) {
  const scrollRef = useRef();
  const [loadedPages, setLoadedPages] = useState(1); // 인피니티 스크롤용 로드된 페이지 수
  const [cachedProblemDetails, setCachedProblemDetails] = useState({}); // 문제 상세 정보 캐시

  // 문제 상세 정보가 변경될 때마다 캐시 업데이트
  useEffect(() => {
    if (problemDetails) {
      setCachedProblemDetails(prev => ({
        ...prev,
        ...problemDetails
      }));
    }
  }, [problemDetails]);

  // 디버깅용 로그 추가
  useEffect(() => {
    if (problemList && problemList.problems) {
      const startIndex = (currentPage - 1) * perPage;
      const endIndex = startIndex + perPage;
    }
  }, [problemList, currentPage, perPage]);

  // 새로운 셀 선택 시 로드된 페이지 수 리셋 (데스크톱/모바일 모두)
  useEffect(() => {
    if (!showOnlySelected) {
      setLoadedPages(1);
      setCachedProblemDetails({}); // 새 셀 선택 시 캐시 초기화
    }
  }, [selectedCell]);

  // 인피니티 스크롤 이벤트 핸들러 (데스크톱/모바일 모두)
  useEffect(() => {
    if (!scrollRef.current) return;

    const handleScroll = () => {
      const element = scrollRef.current;
      if (element.scrollTop + element.clientHeight >= element.scrollHeight - 100) {
        // 하단에 도달했을 때 다음 페이지 로드
        const calculatedTotalPages = Math.ceil(problemList?.problems?.length / perPage) || 1;
        if (loadedPages < calculatedTotalPages && !loadingProblemDetails) {
          const nextPage = loadedPages + 1;
          setLoadedPages(nextPage);
          onPageChange(nextPage);
        }
      }
    };

    const element = scrollRef.current;
    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [loadedPages, problemList, perPage, loadingProblemDetails, onPageChange]);

  if (!selectedCell && !showOnlySelected) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded bg-gray-100 p-4 text-center text-gray-500">
          유형, 난이도를 선택하세요.
        </div>
      </div>
    );
  }

  if (!showOnlySelected && (!problemList || (loadingProblemDetails && loadedPages === 1))) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // 선택된 문제 목록에서 현재 문제의 순서를 찾는 함수
  const getSelectedProblemIndex = (problemId) => {
    const index = selectedProblems.findIndex(p => p.problem_id === problemId);
    return index !== -1 ? index + 1 : null;
  };

  // 검색어 하이라이트 함수
  const highlightSearchTerm = (text) => {
    if (!searchTerm || !text) return text;

    // 검색어를 쉼표로 분리하고 공백 제거
    const searchTerms = searchTerm.split(',').map(term => term.trim()).filter(term => term);

    if (searchTerms.length === 0) return text;

    // 각 검색어에 대해 하이라이트 적용
    let highlightedText = text;
    searchTerms.forEach(term => {
      const regex = new RegExp(`(${term})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<span style="font-weight: bold; color: #4ABC85;">$1</span>');
    });

    return highlightedText;
  };

  // 표시할 문제 목록 결정
  let filteredProblems;
  if (showOnlySelected) {
    // 출제 시험만 보기: selectedProblems를 직접 사용 (셀 선택 불필요)
    filteredProblems = selectedProblems;
  } else {
    const endIndex = loadedPages * perPage;
    filteredProblems = problemList.problems.slice(0, endIndex);
  }

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
      >
        <div className="space-y-10">
          {showOnlySelected && filteredProblems.length === 0 ? (
            <div className="flex h-32 items-center justify-center">
              <p className="text-sm text-gray-500">선택된 문제가 없습니다</p>
            </div>
          ) : null}
          {filteredProblems.map((problem, index) => {
            // 현재 문제가 선택된 문제 목록에 있는지 확인
            const isSelected = selectedProblems.some(p => p.problem_id === problem.problem_id);
            // 선택된 문제의 인덱스 (Preview와 일치하는 번호)
            const selectedIndex = getSelectedProblemIndex(problem.problem_id);
            const isFirst = selectedIndex === 1;
            const isLast = selectedIndex === selectedProblems.length;

            return (
              <div key={problem.problem_id} className="relative">
                <div className="absolute left-2 top-[-18px] flex gap-2 text-[10px] text-gray-500 whitespace-nowrap">
                  <span>ID: {problem.problem_id}</span>
                  <span dangerouslySetInnerHTML={{ __html: `라벨: ${highlightSearchTerm(problem.labels)}` }} />
                </div>
                <div
                  className={`relative mt-5 cursor-pointer rounded-3xl border bg-white p-4 hover:bg-gray-50
                    ${isSelected ? 'border-primary' : ''}`}
                  onClick={() => onProblemClick(problem)}
                >
                  {/* X(제거) 버튼 - 카드 우측 상단 */}
                  {isSelected && onRemoveProblem && (
                    <button
                      className="absolute right-2 top-2 z-10 flex size-7 items-center justify-center rounded-full bg-white text-sm text-gray-400 shadow-sm border border-gray-200 hover:bg-red-100 hover:text-red-500 hover:border-red-300 transition-colors"
                      onClick={(e) => { e.stopPropagation(); onRemoveProblem(problem.problem_id); }}
                      title="문제 제거"
                    >
                      ✕
                    </button>
                  )}
                  <div className="problem-content-wrapper flex">
                    {isSelected && (
                      <div
                        className="problem-number flex flex-col items-center flex-shrink-0"
                        style={{
                          minWidth: '36px',
                          marginRight: '20px',
                        }}
                      >
                        <button
                          className={`text-xs leading-none px-1 py-0.5 rounded transition-colors ${isFirst ? 'invisible' : 'text-gray-400 hover:text-primary'}`}
                          onClick={(e) => { e.stopPropagation(); onReorderProblem && onReorderProblem(problem.problem_id, 'up'); }}
                          title="위로 이동"
                        >
                          ▲
                        </button>
                        <div
                          className="text-2xl"
                          style={{ color: '#4ABC85', fontWeight: 900 }}
                        >
                          {String(selectedIndex).padStart(2, '0')}
                        </div>
                        <button
                          className={`text-xs leading-none px-1 py-0.5 rounded transition-colors ${isLast ? 'invisible' : 'text-gray-400 hover:text-primary'}`}
                          onClick={(e) => { e.stopPropagation(); onReorderProblem && onReorderProblem(problem.problem_id, 'down'); }}
                          title="아래로 이동"
                        >
                          ▼
                        </button>
                      </div>
                    )}
                    <div
                      className="problem-content flex-1 min-w-0"
                      style={{
                        transform: 'scale(0.8)',
                        transformOrigin: 'top left',
                        width: '125%' // scale(0.8)의 역수로 보정
                      }}
                    >
                      {cachedProblemDetails[problem.problem_id] ? (
                        <div
                          className="break-words overflow-wrap-anywhere"
                          dangerouslySetInnerHTML={{
                            __html: cachedProblemDetails[problem.problem_id].content
                          }}
                        />
                      ) : (
                        <p className="text-gray-500">문제 내용을 불러오는 중...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* 로딩 인디케이터 */}
          {loadingProblemDetails && loadedPages > 1 && (
            <div className="flex h-16 items-center justify-center">
              <div className="size-6 animate-spin rounded-full border-b-2 border-gray-900"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProblemSelector; 